import type {
  TagMappingGrammarToken,
  TagMappingTagBreakdown,
  TagMappingTagSegment
} from '@axilon-apps/domains-ontology-ui-state'

export type MappingSide = 'a' | 'b'

export type SegmentKind = 'join' | 'skip' | 'separator' | 'wildcard'

export interface SegmentDecode {
  title: string
  kind: SegmentKind
  kindLabel: string
  summary: string
  captured: string
  typeLabel?: string
  canonical?: string
  aliases?: string[]
  regex?: string
}

const PUNCTUATION = /^[\s.\-_:/]+$/

export function humanizeSlotName(name: string): string {
  const stripped = name.replace(/^_+/, '').replace(/_/g, ' ').trim()
  if (!stripped) return 'Skip'
  return stripped.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, (ch) => ch.toUpperCase())
}

export function isPunctuationLiteral(segment: TagMappingTagSegment): boolean {
  return segment.kind === 'literal' && PUNCTUATION.test(segment.text)
}

export function isClickableSegment(segment: TagMappingTagSegment): boolean {
  return !isPunctuationLiteral(segment)
}

export function segmentKind(
  segment: TagMappingTagSegment,
  joinKeys: readonly string[]
): SegmentKind {
  if (segment.kind === 'literal') return 'separator'
  if (segment.kind === 'wildcard') return 'wildcard'
  if (segment.name && joinKeys.includes(segment.name)) return 'join'
  return 'skip'
}

export function defaultSegmentIndex(
  segments: readonly TagMappingTagSegment[],
  joinKeys: readonly string[]
): number {
  const joinIndex = segments.findIndex(
    (segment) => segment.kind === 'slot' && segment.name != null && joinKeys.includes(segment.name)
  )
  if (joinIndex >= 0) return joinIndex
  const clickable = segments.findIndex(isClickableSegment)
  return clickable >= 0 ? clickable : 0
}

export function joinValuesFromSegments(
  segments: readonly TagMappingTagSegment[],
  joinKeys: readonly string[]
): Record<string, string> {
  const values: Record<string, string> = {}
  for (const key of joinKeys) {
    const hit = segments.find((segment) => segment.kind === 'slot' && segment.name === key)
    if (hit) values[key] = hit.text
  }
  return values
}

export function enumAliasGroups(
  canonical: Record<string, string> | undefined
): Array<{ canonical: string; aliases: string[] }> {
  if (!canonical) return []
  const groups = new Map<string, string[]>()
  for (const [alias, canon] of Object.entries(canonical)) {
    const list = groups.get(canon) ?? []
    if (!list.some((entry) => entry.toUpperCase() === alias.toUpperCase())) list.push(alias)
    groups.set(canon, list)
  }
  return [...groups.entries()].map(([name, aliases]) => ({ canonical: name, aliases }))
}

export function decodeSegment(
  segment: TagMappingTagSegment,
  token: TagMappingGrammarToken | undefined,
  joinKeys: readonly string[]
): SegmentDecode {
  const slotToken = token?.kind === 'slot' ? token : undefined
  const kind = segmentKind(segment, joinKeys)
  const captured = segment.text
  const title =
    segment.kind === 'slot' && segment.name
      ? humanizeSlotName(segment.name)
      : kind === 'wildcard'
        ? 'Skip'
        : 'Separator'
  const typeLabel = typeLabelOf(segment, slotToken)
  const groups = enumAliasGroups(slotToken?.enumCanonical)
  const matchedGroup = groups.find((group) =>
    group.aliases.some((alias) => alias.toUpperCase() === captured.toUpperCase())
  )
  const canonical =
    matchedGroup && matchedGroup.canonical.toUpperCase() !== captured.toUpperCase()
      ? matchedGroup.canonical
      : slotToken?.enumCanonical?.[captured.toUpperCase()]
  const aliases = matchedGroup?.aliases.filter(
    (alias) => alias.toUpperCase() !== captured.toUpperCase()
  )
  const regex = shouldShowRegex(slotToken) ? slotToken?.regex : undefined
  return {
    title,
    kind,
    kindLabel: kindLabelOf(kind),
    summary: summaryOf(kind, captured, canonical, aliases),
    captured,
    ...(typeLabel ? { typeLabel } : {}),
    ...(canonical && canonical.toUpperCase() !== captured.toUpperCase() ? { canonical } : {}),
    ...(aliases && aliases.length > 0 ? { aliases } : {}),
    ...(regex ? { regex } : {})
  }
}

export function samplesForSide(
  side: MappingSide,
  a: readonly TagMappingTagBreakdown[] | undefined,
  b: readonly TagMappingTagBreakdown[] | undefined
): readonly TagMappingTagBreakdown[] {
  return side === 'a' ? (a ?? []) : (b ?? [])
}

const LOOKUP_MIN_CHARS = 2
export const LOOKUP_PAGE_SIZE = 20

export type LookupResultTab = 'links' | 'unmatched' | 'unparsed'

export interface TagLookupMatch {
  aName: string
  bName: string
  slots: Record<string, string>
  unique: boolean
  grammarId?: string
}

export interface TagLookupName {
  side: MappingSide
  name: string
}

export interface TagLookupResult {
  query: string
  matches: TagLookupMatch[]
  unmatched: TagLookupName[]
  unparsed: TagLookupName[]
}

export interface TagLookupSource {
  matches?: readonly TagLookupMatch[]
  manualMatches?: readonly TagLookupMatch[]
  grammars?: ReadonlyArray<{ matches?: readonly TagLookupMatch[] }>
  unmatchedA?: ReadonlyArray<{ name: string }>
  unmatchedB?: ReadonlyArray<{ name: string }>
  unparsedA?: ReadonlyArray<{ name: string }>
  unparsedB?: ReadonlyArray<{ name: string }>
  aNames?: readonly string[]
  bNames?: readonly string[]
}

/** Find links and parse misses for a tag name (or fragment) in a preview. */
export function lookupTagInPreview(preview: TagLookupSource, rawQuery: string): TagLookupResult {
  const query = rawQuery.trim()
  if (query.length > 0 && query.length < LOOKUP_MIN_CHARS) {
    return { query, matches: [], unmatched: [], unparsed: [] }
  }
  const allMatches = uniqueMatches([
    ...(preview.matches ?? []),
    ...(preview.manualMatches ?? []),
    ...(preview.grammars ?? []).flatMap((grammar) => grammar.matches ?? [])
  ])
  const searching = query.length >= LOOKUP_MIN_CHARS
  const matches = searching
    ? uniqueMatches(
        allMatches
          .filter((row) => nameHits(row.aName, query) || nameHits(row.bName, query))
          .sort((a, b) => rankMatch(a, query) - rankMatch(b, query))
      )
    : allMatches
  const linked = namesFromMatches(allMatches)
  const unmatchedSource = [
    ...(preview.unmatchedA ?? []).map((row) => ({ side: 'a' as const, name: row.name })),
    ...(preview.unmatchedB ?? []).map((row) => ({
      side: 'b' as const,
      name: row.name
    }))
  ]
  const unmatched = searching
    ? lookupNames(unmatchedSource, query, linked)
    : listedNames(unmatchedSource, linked)
  const claimed = namesFromMatches(allMatches)
  for (const row of preview.unmatchedA ?? []) claimed.add(foldName(row.name))
  for (const row of preview.unmatchedB ?? []) claimed.add(foldName(row.name))
  const unparsed = searching
    ? lookupNames(unparsedNames(preview), query, claimed)
    : listedNames(unparsedNames(preview), claimed)
  return { query, matches, unmatched, unparsed }
}

function unparsedNames(preview: TagLookupSource): TagLookupName[] {
  if ((preview.aNames?.length ?? 0) > 0 || (preview.bNames?.length ?? 0) > 0) {
    return [
      ...(preview.aNames ?? []).map((name) => ({ side: 'a' as const, name })),
      ...(preview.bNames ?? []).map((name) => ({ side: 'b' as const, name }))
    ]
  }
  return [
    ...(preview.unparsedA ?? []).map((row) => ({ side: 'a' as const, name: row.name })),
    ...(preview.unparsedB ?? []).map((row) => ({
      side: 'b' as const,
      name: row.name
    }))
  ]
}

function namesFromMatches(matches: readonly TagLookupMatch[]): Set<string> {
  const names = new Set<string>()
  for (const row of matches) {
    names.add(foldName(row.aName))
    names.add(foldName(row.bName))
  }
  return names
}

function lookupNames(
  names: readonly TagLookupName[],
  query: string,
  skip: ReadonlySet<string>
): TagLookupName[] {
  return names
    .filter((row) => nameHits(row.name, query) && !skip.has(foldName(row.name)))
    .sort(
      (a, b) => rankName(a.name, query) - rankName(b.name, query) || a.name.localeCompare(b.name)
    )
}

function listedNames(names: readonly TagLookupName[], skip: ReadonlySet<string>): TagLookupName[] {
  return names
    .filter((row) => !skip.has(foldName(row.name)))
    .sort((a, b) => a.side.localeCompare(b.side) || a.name.localeCompare(b.name))
}

export function counterpartNames(
  preview: TagLookupSource,
  side: MappingSide,
  rawQuery: string
): string[] {
  const names = side === 'a' ? (preview.bNames ?? []) : (preview.aNames ?? [])
  const query = rawQuery.trim()
  if (query.length > 0 && query.length < LOOKUP_MIN_CHARS) return []
  const filtered =
    query.length >= LOOKUP_MIN_CHARS ? names.filter((name) => nameHits(name, query)) : [...names]
  return filtered.sort(
    (a, b) => (query ? rankName(a, query) - rankName(b, query) : 0) || a.localeCompare(b)
  )
}

export function namesWithNoGrammar(preview: TagLookupSource): TagLookupName[] {
  const claimed = namesFromMatches([
    ...(preview.matches ?? []),
    ...(preview.manualMatches ?? []),
    ...(preview.grammars ?? []).flatMap((grammar) => grammar.matches ?? [])
  ])
  for (const row of preview.unmatchedA ?? []) claimed.add(foldName(row.name))
  for (const row of preview.unmatchedB ?? []) claimed.add(foldName(row.name))
  return unparsedNames(preview)
    .filter((row) => !claimed.has(foldName(row.name)))
    .sort((a, b) => a.side.localeCompare(b.side) || a.name.localeCompare(b.name))
}

export function defaultLookupTab(result: TagLookupResult): LookupResultTab {
  if (result.matches.length > 0) return 'links'
  if (result.unmatched.length > 0) return 'unmatched'
  return 'unparsed'
}

export function pageCount(total: number, pageSize = LOOKUP_PAGE_SIZE): number {
  if (total <= 0) return 1
  return Math.ceil(total / pageSize)
}

export function clampPage(page: number, total: number, pageSize = LOOKUP_PAGE_SIZE): number {
  return Math.min(Math.max(0, page), pageCount(total, pageSize) - 1)
}

export function pageSlice<T>(items: readonly T[], page: number, pageSize = LOOKUP_PAGE_SIZE): T[] {
  const start = clampPage(page, items.length, pageSize) * pageSize
  return items.slice(start, start + pageSize)
}

function uniqueMatches(rows: readonly TagLookupMatch[]): TagLookupMatch[] {
  const seen = new Set<string>()
  const out: TagLookupMatch[] = []
  for (const row of rows) {
    const key = `${row.aName}\0${row.bName}\0${row.grammarId ?? ''}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(row)
  }
  return out
}

function nameHits(name: string, query: string): boolean {
  return foldName(name).includes(foldName(query))
}

function rankMatch(row: TagLookupMatch, query: string): number {
  return Math.min(rankName(row.aName, query), rankName(row.bName, query))
}

function rankName(name: string, query: string): number {
  const folded = foldName(name)
  const needle = foldName(query)
  if (folded === needle) return 0
  if (folded.startsWith(needle)) return 1
  return 2
}

function foldName(name: string): string {
  return name.trim().toUpperCase()
}

function typeLabelOf(
  segment: TagMappingTagSegment,
  token: TagMappingGrammarToken | undefined
): string | undefined {
  const slotToken = token?.kind === 'slot' ? token : undefined
  const typeName = slotToken?.typeName ?? segment.typeName
  if (!typeName) return undefined
  if (typeName === 'int') return 'Integer'
  if (typeName === 'string') return 'Text'
  const count = slotToken?.enumValues?.length
  return count != null && count > 0 ? `Enum · ${count} values` : `Enum · ${typeName}`
}

function kindLabelOf(kind: SegmentKind): string {
  if (kind === 'join') return 'Join key'
  if (kind === 'wildcard') return 'Skip'
  if (kind === 'separator') return 'Separator'
  return 'Not joined'
}

function summaryOf(
  kind: SegmentKind,
  captured: string,
  canonical: string | undefined,
  aliases: string[] | undefined
): string {
  if (kind === 'join' && canonical && canonical.toUpperCase() !== captured.toUpperCase()) {
    return `Join key — captured as ${captured}, linked as ${canonical}.`
  }
  if (kind === 'join' && aliases && aliases.length > 0) {
    return `Join key — also matches ${aliases.join(', ')}.`
  }
  if (kind === 'join') return 'Join key — A and B must agree on this value to link.'
  if (kind === 'wildcard') return 'Skipped when parsing. Not used to join.'
  if (kind === 'separator') return 'Fixed text in this naming convention.'
  return 'Parsed from the name but not used as a join key.'
}

function shouldShowRegex(token: TagMappingGrammarToken | undefined): boolean {
  if (token?.kind !== 'slot' || !token.regex) return false
  if ((token.enumValues?.length ?? 0) > 0) return false
  return token.regex.length <= 40
}
